import {
  Card as MuiCard,
  Box,
  Typography as Typo,
  Paper,
  useTheme,
  styled,
  Link,
  TypographyProps,
  Menu,
  MenuItem,
  // IconButton,
  ButtonProps,
  Divider,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {useState, useMemo, useEffect, use} from 'react';
import {Image, ImageProps} from '@jobseeker/lotus/components/Image';
import type {JobListProps} from '../type';
import {
  YellowCrown,
  DefaultJobLogo,
  Dot,
  ThreeDotMenuIcon,
  CheckCircle,
  RupeeGrey,
} from '@jobseeker/lotus/icons';
import {getRandomColor} from '@jobseeker/lotus/theme';
import JobTag from '../V2/JobTags';
import {dateToDuration} from '@jobseeker/lotus/utils/date';
import {customKebab, formatSalary} from '@jobseeker/lotus/utils';
import NextLink from 'next/link';
import {FaStar, FaRegStar, FaInfo, FaBookmark} from 'react-icons/fa';
import {RemoveCircle} from '@mui/icons-material';
import {GoDotFill} from 'react-icons/go';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {Button, Tooltip} from '@jobseeker/lotus/elements';
import {TiInfoLarge} from 'react-icons/ti';
import {
  bgcolor,
  border,
  color,
  fontSize,
  fontWeight,
  textAlign,
} from '@mui/system';
import {isArray} from 'lodash';
import {boolean} from 'yup';
import {RemoveIcon} from '@jobseeker/lotus/assets/icons';

const IconButton = styled(Button)<
  ButtonProps | {active: boolean; primaryColor: string}
>(({theme, variant, active, primaryColor}: any) => ({
  padding: theme.spacing(1),
  fontSize: theme.typography.pxToRem(16),
  color: variant === 'contained' ? '#fff' : primaryColor,
  borderColor: primaryColor,
  opacity: variant === 'contained' || active ? 1 : 0.3,
  backgroundColor: variant === 'contained' ? primaryColor : 'transparent',
  ':hover': {
    borderColor: primaryColor,
    backgroundColor: variant === 'contained' ? primaryColor : 'transparent',
  },
}));

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));
interface ImageWithFallbackProps extends ImageProps {
  fallback: any;
  itemId: any;
}
export const ImageWithFallback = ({
  fallback,
  alt,
  src,
  ...props
}: ImageWithFallbackProps) => {
  const [error, setError] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setError(false);
  }, [src]);

  const randomColor = useMemo(() => getRandomColor(props.itemId), [src]);

  return (
    <>
      {!error ? (
        <Image
          key={alt}
          src={src}
          alt={alt}
          loading="lazy"
          onErrorCapture={() => setError(true)}
          style={{
            backgroundColor: '#fff',
          }}
          {...props}
        />
      ) : (
        <Box
          key={`${alt}_${randomColor}`}
          className="joblist__logo"
          sx={{
            background: randomColor,
            p: theme.spacing(1.5),
            height: 'min-content',
            lineHeight: 0,
          }}
        >
          <Image
            src={DefaultJobLogo}
            alt={'Logo'}
            width="25"
            height={'25'}
            objectFit="cover"
            quality={50}
          />
        </Box>
      )}
    </>
  );
};

export const Typography = styled(Typo)<any>((props: TypographyProps) => ({
  display: 'flex',
  alignItems: 'center',
}));

export const JoblistPaper = styled(Paper)<any>((props: any) => ({
  borderRadius: 0,
  position: 'relative',
  display: 'flex',
  gap: props.theme.spacing(1.5),
  // cursor: 'pointer',
  '.joblist__logo': {
    borderRadius: props.theme.spacing(1.5),
    border: '0.6px solid #7E7E7E33',
  },
  '.joblist__right_element': {
    position: 'absolute',
    width: 'fit-content',
    right: props.theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
    // padding: props.theme.spacing(2),
    top: 0,
    bottom: 0,
  },
  '.joblist__subtitle': {
    p: {
      wordBreak: 'break-all',
      color: props.theme.palette.text.secondary,
    },
  },
  '.save-icon': {
    display: 'none',
  },
  ':hover': {
    '.save-icon': {
      display: 'unset',
    },
  },
}));

const JobListComponent = (props: JobListProps) => {
  const theme = useTheme();
  const {
    date,
    jobData,
    // isDetailCard,
    showSaveIcon = true,
    showThreeDotMenu = false,
    showApplied = false,
    redirectionLink = '',
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const IsLarge = useIsLargeDevice();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  useEffect(() => {
    const handler = () => {
      setOpen(false);
    };
    window?.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };
  const dateString = useMemo(() => {
    return dateToDuration(date, true);
  }, [date]);

  const savedDateString = useMemo(() => {
    return dateToDuration(props.jobData?.savedDate as string, true);
  }, [props.jobData?.savedDate]);

  const savedDateBDF = useMemo(() => {
    const date = props.jobData?.savedDate;
    if (!date) return '';

    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    return dateObj.toLocaleDateString('en-GB');
  }, [props.jobData?.savedDate]);
  const salaryString = formatSalary(
    props.jobData?.minSal,
    props.jobData?.maxSal,
    props.jobData?.hideSal
  );

  const randomColor = useMemo(
    () => getRandomColor(props.jobData?.id),
    [props.companyImgUrl, props.jobData?.companyData]
  );
  const MainComponent = Card;

  const onJobClick = (event: any) => {
    // alert('test');
    event?.stopPropagation();
    if (props.onJobClick) {
      // props.onJobClick(props.jobData);
    }
  };

  const mandatoryTags = useMemo(() => {
    return props.jobData?.tags?.filter((tag: any) => !!tag?.isMandatory);
  }, [props.jobData?.tags]);

  const onSaveClick = (event: any) => {
    event?.preventDefault();
    event?.stopPropagation();
    // setSaved(!saved);
    if (props.onSaveClick) {
      props.onSaveClick(props.jobData, !!props.jobData?.saved);
    }
  };
  const isLarge = useIsLargeDevice();
  const isMedium = useIsLargeDevice(1024);
  const isSmall = useIsLargeDevice(425);
  const hirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

  const imageLoader = ({src}: {src: string}) => {
    return props.companyImgUrl ?? '';
  };

  let href = `/j/${customKebab(props?.jobData?.title)}-${
    props?.jobData?.id
  }?ref=${props?.refCode}`;

  if (redirectionLink.length > 0) {
    href = redirectionLink;
  }

  return (
    <MainComponent
      className="joblist-card-v2"
      color="gray.dark"
      onClick={onJobClick}
      sx={{
        position: 'relative',
        overflow: 'unset',
        transition: 'all ease 0.2s',
        cursor: 'pointer',
        ml: 3,
        ':hover': {
          boxShadow: hirist ? 'none' : 'rgba(0, 0, 0, 0.09) 0px 5px 15px 0px;',
        },
      }}
    >
      <a
        href={href}
        target="_blank"
        style={{
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <JoblistPaper
          elevation={0}
          sx={{
            ...props.sx,
            py: {xxs: 1.25},
            borderRadius: 2,
            pl: 5,
            pr: 0,
            border: jobData?.applied ? '1px solid #49c5b6' : '',
          }}
        >
          {!!jobData?.applied && (
            <Box
              sx={{
                // display: 'flex',
                position: 'absolute',
                top: '-8px',
                left: '45px',
                color: '#fff',
                bgcolor: '#49c5b6',
                // border: '1px solid purple',
                lineHeight: '14px',
                borderRadius: '3px',
                zIndex: 1,
                display: 'inline-block',
              }}
            >
              <Typography
                sx={{
                  fontSize: '10px',
                  py: '2px',
                  px: '4px',
                  color: '#fff',
                  fontWeight: 500,
                  bgcolor: '#49c5b6',
                }}
              >
                APPLIED
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              position: 'absolute',
              left: '-25px',
              top: theme.spacing(1.25),
            }}
          >
            {props.companyImgUrl ? (
              <ImageWithFallback
                className="joblist__logo"
                src={
                  props.jobData?.confidential ? '' : (props.companyImgUrl ?? '')
                }
                fallback={
                  props.jobData?.confidential ? '' : props.fallbackCompanyUrl
                }
                alt={`logo_${jobData?.id}`}
                width="50"
                height={'50'}
                objectFit="cover"
                itemId={jobData?.id}
                quality={50}
                placeholder="blur"
                blurDataURL={props.companyImgUrl}
                loader={imageLoader}
              />
            ) : (
              <Box
                className="joblist__logo"
                sx={{
                  background: randomColor,
                  p: theme.spacing(1.5),
                  height: 'min-content',
                  lineHeight: 0,
                  border: '1px solid brown',
                }}
              >
                <Image
                  src={DefaultJobLogo}
                  alt={'Logo'}
                  width="25"
                  height={'25'}
                  objectFit="cover"
                  quality={50}
                />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {!isMedium && (
              <>
                <Typography
                  component={'span'}
                  data-testid="job_experience"
                  sx={{lineHeight: '19px', mb: '2px'}}
                  variant="body2"
                  fontWeight="300"
                >
                  {props.jobData?.confidential
                    ? 'Verified Recruiter'
                    : jobData?.companyData
                      ? jobData?.companyData?.companyName
                      : jobData?.companyStatus == 1
                        ? jobData?.title.split('-')[0]
                        : 'Consultant'}
                </Typography>
              </>
            )}
            <Box
              // sx={{pr: {xxs: 3, sm: 12.2}}}
              display={'flex'}
              justifyContent={'space-between'}
              gap={2}
              alignItems={'flex-start'}
            >
              <Typography
                component={'p'}
                data-testid="job_title"
                variant="subtitle2"
                fontSize={{xxs: 16}}
                lineHeight="21px"
                fontWeight={500}
                width={'90%'}
                sx={{
                  lineClamp: '3',
                  '-webkit-line-clamp': '2',
                  '-webkit-box-orient': 'vertical',
                  // display: '-webkit-flex',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                  display: 'block',
                  alignItems: 'center',
                }}
              >
                {props.title}
              </Typography>
            </Box>
            <Box
              className="joblist__subtitle"
              sx={{
                display: 'flex',
                columnGap: 1,
                rowGap: 0.5,
                alignItems: 'center',
                justifyContent: 'space-between',
                color: theme.palette.text.secondary,
                flexWrap: 'nowrap',
                mb: '-2px',
                mt: '4px',
              }}
            >
              {/* <Box display={'flex'} gap={1} flexDirection={{xxs: 'column'}}> */}
              <Box
                sx={{display: 'flex', alignItems: 'center'}}
                flexWrap={'wrap'}
              >
                {isMedium && (
                  <>
                    <Typography
                      component={'span'}
                      data-testid="job_experience"
                      sx={{lineHeight: '16px'}}
                      variant="body2"
                      fontWeight="300"
                    >
                      {props.jobData?.confidential
                        ? 'Verified Recruiter'
                        : jobData?.companyData
                          ? jobData?.companyData.companyName
                          : jobData?.companyStatus == 1
                            ? jobData?.title.split('-')[0]
                            : 'Consultant'}
                    </Typography>
                    <Typography component={'span'} color="#cecece" mx={1}>
                      <GoDotFill size={9} />
                    </Typography>
                  </>
                )}
                <Typography
                  component={'span'}
                  data-testid="job_experience"
                  sx={{lineHeight: '16px'}}
                  variant="body2"
                  fontWeight="300"
                >
                  {props.experiance}
                </Typography>
                {salaryString && (
                  <>
                    <Typography component={'span'} color="#cecece" mx={1}>
                      <GoDotFill size={9} />
                    </Typography>
                    <Typography
                      component={'span'}
                      data-testid="job_salary"
                      sx={{lineHeight: '16px'}}
                      variant="body2"
                      fontWeight="300"
                    >
                      <Box
                        component="span"
                        sx={{display: 'inline-flex', alignItems: 'center'}}
                      >
                        <Image
                          src={RupeeGrey}
                          alt="rupee"
                          width={15}
                          height={15}
                          style={{marginRight: 1, marginLeft: -1}}
                        />
                        {salaryString}
                      </Box>
                    </Typography>
                  </>
                )}
                <Typography component={'span'} color="#cecece" mx={1}>
                  <GoDotFill size={9} />
                </Typography>

                {props.location === 'Multiple Locations' ? (
                  <MuiTooltip
                    placement="bottom"
                    title={props.jobData?.location?.map((loc, index) => (
                      <Typography
                        variant="subtitle2"
                        sx={{
                          textAlign: 'center',
                          whiteSpace: 'normal',
                          overflowWrap: 'break-word',
                          backgroundColor: '#1c2d41',
                          color: '#fff',
                          padding: '1px',
                          borderRadius: '4px',
                        }}
                        key={index}
                      >
                        ▪ &nbsp;{loc.name}
                      </Typography>
                    ))}
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: '#1c2d41',
                          color: '#fff',
                          p: '16px',
                        },
                      },
                      arrow: {
                        sx: {
                          color: '#1c2d41',
                        },
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          // maxWidth: '100%',
                          overflow: 'hidden',
                          position: 'relative',
                          display: 'inline-block',
                          textOverflow: 'ellipsis',
                          lineHeight: '16px',
                          mb: '-3px',
                          // whiteSpace: 'nowrap',
                        }}
                        component={'p'}
                        data-testid="job_location"
                        variant="body2"
                        fontWeight="300"
                      >
                        {props.location}
                      </Typography>
                    </Box>
                  </MuiTooltip>
                ) : (
                  <Typography
                    sx={{
                      // maxWidth: '100%',
                      overflow: 'hidden',
                      position: 'relative',
                      display: 'inline-block',
                      textOverflow: 'ellipsis',
                      lineHeight: '16px',
                      // whiteSpace: 'nowrap',
                    }}
                    component={'p'}
                    data-testid="job_location"
                    variant="body2"
                    fontWeight="300"
                  >
                    {props.location}
                  </Typography>
                )}

                <Typography component={'span'} color="#cecece" mx={1}>
                  <GoDotFill size={9} />
                </Typography>
                <Typography
                  sx={{
                    // maxWidth: '100%',
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'inline-block',
                    textOverflow: 'ellipsis',
                    lineHeight: '16px',
                    // whiteSpace: 'nowrap',
                  }}
                  component={'p'}
                  data-testid="job_location"
                  variant="body2"
                  fontWeight="300"
                >
                  {savedDateBDF}
                </Typography>
              </Box>
              {/* </Box> */}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'start',
              gap: '10px',
              padding: '0 10px',
              color: '#b4b9d8',
            }}
          >
            {isSmall ? (
              <IconButton
                onClick={onSaveClick}
                aria-label="toggle password visibility"
                disableRipple
                disableFocusRipple
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&:active': {
                    backgroundColor: 'transparent',
                  },
                  '&:focus': {
                    backgroundColor: 'transparent',
                  },
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: 1,
                  p: 0,
                }}
              >
                <Image
                  src={RemoveIcon}
                  alt="removeicon"
                  width={32}
                  height={32}
                />
                <Typography
                  component={'span'}
                  fontSize={9}
                  color="#b4b9d8"
                  fontWeight={700}
                  mt="6px"
                >
                  REMOVE
                </Typography>
              </IconButton>
            ) : (
              <Typography
                component={'span'}
                fontSize={9}
                color="#b4b9d8"
                fontWeight={700}
                onClick={onSaveClick}
              >
                REMOVE
              </Typography>
            )}
          </Box>
        </JoblistPaper>
      </a>
    </MainComponent>
  );
};

export default JobListComponent;
